import { useState } from 'react';
import { Button } from '@orion/ui';
import { PageHeader } from '../../app/shell.js';

function Card({ dot, label, meta, children }: { dot: string; label: string; meta: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col overflow-hidden rounded-card border border-border bg-surface p-4 shadow-card">
      <div className="flex shrink-0 items-center justify-between pb-2">
        <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">
          <span className="size-1.5 rounded-full" style={{ background: dot }} />
          {label}
        </span>
        <span className="text-xs text-text-muted">{meta}</span>
      </div>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </section>
  );
}

function IconBadge({ bg, fg, path, rect }: { bg: string; fg: string; path: string; rect?: boolean }) {
  return (
    <span className="mb-1 flex size-11 items-center justify-center rounded-input" style={{ background: bg }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={fg} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {rect && <rect x="4" y="11" width="16" height="10" rx="2" />}
        <path d={path} />
      </svg>
    </span>
  );
}

/** docs/architecture/43 "Empty states" — design/orion-crm-design/rendered-
 * html/EmptyError.html is a 2×2 reference sheet of four independent UI
 * patterns (empty/no-results/error/permission-denied), never all four at
 * once in the real app — the same "Foundations" documentation role Main/
 * Sidebar/Topbar/AdminNav already play, not a live data-bound screen. Real
 * `EmptyState`/`Button` components render every card; the copy is the
 * design's own illustrative example text (a lead count, a rep's name, an
 * error reference), same as ComponentLibraryPage's other sample content —
 * there's no live "128 leads" or "ERR-7F3A21" this page could bind to
 * without inventing one. Copying the error reference is real
 * (`navigator.clipboard`). */
export function EmptyErrorStatesPage() {
  const [copied, setCopied] = useState(false);

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'Component library', to: '/component-library' }, { label: 'Empty, error & permission states' }]} />
      <main className="grid flex-1 grid-cols-2 grid-rows-2 gap-4 overflow-hidden px-7 py-6">
        <Card dot="var(--color-primary)" label="Empty · first run" meta="Leads list, workspace has 0 leads">
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
            <IconBadge bg="var(--color-primary-soft)" fg="var(--color-primary)" path="M13 2 3 14h7l-1 8 11-14h-7z" />
            <h2 className="text-lg font-[650] tracking-[-0.01em] text-ink">No leads yet</h2>
            <p className="max-w-80 text-sm leading-5 text-text-muted">Capture your first lead and start building your pipeline.</p>
            <div className="mt-2 flex gap-2">
              <Button size="sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Create lead
              </Button>
              <Button size="sm" variant="secondary">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 16V4M7 9l5-5 5 5" />
                  <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
                </svg>
                Import leads
              </Button>
            </div>
            <a href="/admin/web-forms" className="mt-1 flex items-center gap-1.5 text-[13px] font-semibold text-primary-soft-text hover:underline">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-soft-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
                <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
              </svg>
              Connect a web form
            </a>
          </div>
        </Card>

        <Card dot="var(--color-amber-deep)" label="No results" meta="Filters applied to 128 leads">
          <div className="flex flex-wrap items-center gap-1.5 pb-3">
            {['Status: Qualified', 'Owner: Hana Kim', 'Created: Last 7 days'].map((f) => (
              <span key={f} className="flex h-7 items-center gap-1 rounded-pill py-0 pl-2.5 pr-1.5 text-xs font-semibold" style={{ background: 'var(--color-primary-soft)', color: 'var(--color-primary-soft-text)' }}>
                {f}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-soft-text)" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
                  <path d="M6 6l12 12M18 6 6 18" />
                </svg>
              </span>
            ))}
          </div>
          <div className="flex flex-1 flex-col items-center justify-center gap-1.5 text-center">
            <IconBadge bg="var(--color-amber-soft)" fg="var(--color-amber-text)" path="m20 20-3.5-3.5M8.5 11h5" />
            <h2 className="text-base font-[650] text-ink">No leads match these filters</h2>
            <p className="max-w-85 text-[13px] leading-4.75 text-text-muted">Hana has no qualified leads created in the last 7 days. Try removing a filter or widening the date range.</p>
            <div className="mt-1 flex gap-2">
              <Button size="sm" variant="secondary">
                Clear filters
              </Button>
              <button type="button" className="text-[13px] font-semibold text-primary-soft-text hover:underline">
                Remove "Last 7 days"
              </button>
            </div>
          </div>
          <div className="flex shrink-0 flex-col gap-1.5 border-t border-divider pt-3">
            <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">
              <span className="size-1.5 rounded-full bg-text-faint" />
              Loading skeleton
            </span>
            {[1, 0.75, 0.5].map((opacity) => (
              <div key={opacity} className="grid h-6 grid-cols-[20px_1.6fr_1.3fr_0.8fr_1fr] items-center gap-4" style={{ opacity }}>
                <span className="h-3.5 rounded-xs bg-divider" />
                <span className="h-2.5 w-4/5 rounded-pill bg-border" />
                <span className="h-2.5 w-3/4 rounded-pill bg-divider" />
                <span className="h-4.5 rounded-pill bg-divider" />
                <span className="h-2.5 w-3/5 rounded-pill bg-divider" />
              </div>
            ))}
          </div>
        </Card>

        <Card dot="var(--color-danger)" label="Error · failed to load" meta="Server returned 503">
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
            <IconBadge bg="var(--color-danger-soft)" fg="var(--color-danger)" path="M5 16a4 4 0 0 1 .9-7.9A6 6 0 0 1 17.5 7 4.5 4.5 0 0 1 19 15.8 M12 12v4M12 19h.01" />
            <h2 className="text-lg font-[650] tracking-[-0.01em] text-ink">Something went wrong loading leads</h2>
            <p className="max-w-95 text-sm leading-5 text-text-muted">This is on our side, not yours — your data is safe. Give it a moment and try again.</p>
            <div className="mt-2 flex gap-2">
              <Button size="sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 12a8 8 0 1 1-2.3-5.7L20 8" />
                  <path d="M20 3v5h-5" />
                </svg>
                Try again
              </Button>
              <Button size="sm" variant="secondary">
                Go to dashboard
              </Button>
            </div>
            <div className="mt-3 flex h-8 items-center gap-2 rounded-control border border-border bg-surface-subtle py-0 pl-3 pr-1 text-xs text-text-muted">
              Reference <span className="font-mono text-ink">ERR-7F3A21</span>
              {copied && (
                <span className="flex items-center gap-1 font-semibold text-success">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m5 12 5 5 9-10" />
                  </svg>
                  copied
                </span>
              )}
              <button
                type="button"
                aria-label="Copy error reference"
                onClick={() => {
                  void navigator.clipboard?.writeText('ERR-7F3A21');
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="flex size-6 items-center justify-center rounded-xs hover:bg-surface"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="8" y="8" width="12" height="12" rx="2" />
                  <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
                </svg>
              </button>
            </div>
            <span className="text-xs text-text-muted">If this keeps happening, contact your admin.</span>
          </div>
        </Card>

        <Card dot="var(--color-violet)" label="Permission · no access" meta="Role-based, enforced server-side">
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
            <IconBadge bg="var(--color-violet-soft)" fg="var(--color-violet)" path="M8 11V7a4 4 0 0 1 8 0v4M12 15v2" rect />
            <h2 className="text-lg font-[650] tracking-[-0.01em] text-ink">You don't have access to Reports</h2>
            <p className="max-w-95 text-sm leading-5 text-text-muted">
              Your role (Sales) can't view team reports. Ask an admin to grant <b className="font-semibold text-ink">Reports › View</b>.
            </p>
            <div className="mt-2 flex gap-2">
              <Button size="sm">Request access</Button>
              <button type="button" className="flex h-9 items-center rounded-control px-3 text-sm font-semibold text-primary-soft-text hover:bg-primary-soft">
                Back to dashboard
              </button>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-text-muted">
              <span className="flex size-6 items-center justify-center rounded-full bg-danger text-[10px] font-[650] text-white">PN</span>
              Request goes to <b className="font-semibold text-ink">Priya Nair</b> · Admin
            </div>
          </div>
        </Card>
      </main>
    </>
  );
}
